import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { JsignalService } from 'src/app/shared/services/jsignal.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'app-action-status-change-dialog',
    templateUrl: './action-status-change-dialog.component.html',
    styleUrls: ['./action-status-change-dialog.component.scss'],
    standalone: false
})
export class ActionStatusChangeDialogComponent implements OnInit {

  form!: FormGroup;

  action!: any;

  currentState = '';
  selectedState = '';

  showReasonField = false;
  showOutcomeField = false;
  showOtherReasonField = false;
  showOtherOutcomeField = false;

  actionStatusList: any[] = [];
  reasonList: any[] = [];
  actionOutcomeList: any[] = [];

  formSubmitted = false; // ADDED: Track form submission

  lookups = [
    { key: "ActionStatuses", assignTo: "actionStatusList" },
    { key: "ActionReasons", assignTo: "reasonList" },
    { key: "ActionOutcomes", assignTo: "actionOutcomeList" }
  ];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ActionStatusChangeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private jsignalService: JsignalService,
    private commonService: CommonService,
    private spinner: NgxUiLoaderService,
    private snackBar: MatSnackBar
  ) {

    if (data?.action) {
      this.action = data.action;
      this.currentState = this.action.state;
    }
  }

  ngOnInit(): void {
    this.initForm();
    this.loadDropdowns();
    
    // IMPORTANT: Reset form submission flag when dialog opens
    this.formSubmitted = false;
    
    // DO NOT call markAllAsTouched() or any validation triggering here
  }

  initForm(): void {
    this.form = this.fb.group({
      id: [this.action?.id],
      state: ['', Validators.required],
      reasonText: [''],
      otherReasonText: [''],
      actionOutcome: [''],
      otherOutcomeText: ['']
    });
    
    // Set initial values without triggering validation
    if (this.action?.state) {
      this.form.patchValue({ state: this.action.state });
    }
  }

  // ADDED: Helper method to check if field should show error
  shouldShowError(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control && control.invalid && (control.touched || this.formSubmitted));
  }

  onStateChange(state: string): void {
    this.selectedState = state;

    this.showReasonField = state === 'Cancelled' || state === 'Rejected';
    this.showOutcomeField = state === 'Completed';
    this.showOtherReasonField = false;
    this.showOtherOutcomeField = false;

    this.form.patchValue({
      reasonText: '',
      otherReasonText: '',
      actionOutcome: '',
      otherOutcomeText: ''
    });

    this.updateValidators(state);
  }

  onReasonChange(reason: string): void {
    this.showOtherReasonField = reason?.toLowerCase().includes('other');

    if (!this.showOtherReasonField) {
      this.form.patchValue({ otherReasonText: '' });
    }

    this.updateReasonValidators();
  }

  onOutcomeChange(outcome: string): void {
    this.showOtherOutcomeField = outcome?.toLowerCase().includes('other');

    if (!this.showOtherOutcomeField) {
      this.form.patchValue({ otherOutcomeText: '' });
    }

    this.updateOutcomeValidators();
  }

  getReasonList(): any[] {
    return this.reasonList;
  }

  updateValidators(state: string): void {
    const reasonCtrl = this.form.get('reasonText');
    const outcomeCtrl = this.form.get('actionOutcome');

    reasonCtrl?.clearValidators();
    outcomeCtrl?.clearValidators();

    if (state === 'Cancelled' || state === 'Rejected') {
      reasonCtrl?.setValidators([Validators.required]);
    }

    if (state === 'Completed') {
      outcomeCtrl?.setValidators([Validators.required]);
    }

    reasonCtrl?.updateValueAndValidity();
    outcomeCtrl?.updateValueAndValidity();
  }

  updateReasonValidators(): void {
    const ctrl = this.form.get('otherReasonText');

    if (this.showOtherReasonField) {
      ctrl?.setValidators([Validators.required]);
    } else {
      ctrl?.clearValidators();
      ctrl?.setValue('');
    }

    ctrl?.updateValueAndValidity();
  }

  updateOutcomeValidators(): void {
    const ctrl = this.form.get('otherOutcomeText');

    if (this.showOtherOutcomeField) {
      ctrl?.setValidators([Validators.required]);
    } else {
      ctrl?.clearValidators();
      ctrl?.setValue('');
    }

    ctrl?.updateValueAndValidity();
  }

  loadDropdowns(): void {
    this.spinner.start();
    let completed = 0;

    this.lookups.forEach(lookup => {
      this.commonService.getlookupVauleByLookUpType(lookup.key)
        .subscribe({
          next: (res: any) => {
            (this as any)[lookup.assignTo] = Array.isArray(res) ? res : [];
            completed++;
            if (completed === this.lookups.length) {
              this.spinner.stop();
            }
          },
          error: () => {
            completed++;
            if (completed === this.lookups.length) {
              this.spinner.stop();
            }
          }
        });
    });
  }

  submit(): void {
    // Mark form as submitted to trigger validation errors
    this.formSubmitted = true;

    if (this.form.invalid) {
      // Mark all fields as touched to show validation errors
      this.form.markAllAsTouched();
      
      this.snackBar.open(
        'Please fill in all required fields',
        'OK',
        { duration: 3000 }
      );
      
      return;
    }

    this.spinner.start();

    const v = this.form.value;

    let reason = v.reasonText;
    if (this.showOtherReasonField && v.otherReasonText) {
      reason = v.otherReasonText;
    }

    let outcome = v.actionOutcome;
    if (this.showOtherOutcomeField && v.otherOutcomeText) {
      outcome = v.otherOutcomeText;
    }

    const dto: any = {
      state: v.state,
      reason: reason,
      outcome: outcome
    };

    this.jsignalService.updateActionstate(v.id, dto)
      .subscribe({
        next: (response: any) => {
          this.spinner.stop();
          
          // Handle response with statusMessage field
          const message = response?.statusMessage || response?.message || "State updated successfully";
          const isError = response?.error === true;
          
          if (isError) {
            this.snackBar.open(message, 'OK', { duration: 3000, panelClass: ['error-snackbar'] });
          } else {
            this.snackBar.open(message, 'OK', { duration: 3000 });
            this.dialogRef.close(true);
          }
        },
        error: err => {
          this.spinner.stop();
          const errorMessage = err.error?.statusMessage || err.error?.message || "Update failed";
          this.snackBar.open(errorMessage, 'OK', { duration: 3000, panelClass: ['error-snackbar'] });
        }
      });
  }

  close(): void {
    this.dialogRef.close(false);
  }
}